import type React from 'react';
import { type ReactNode, useEffect, useState } from 'react';
import type { FileFormatApiRow } from '@/api/file-formats';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { mapFileFormatApiError } from '@/lib/file-format-errors';
import { formatScopeLabelForDelete } from './file-format-form.utils';

interface FileFormatConfirmDialogProps {
  open: boolean;
  row: FileFormatApiRow | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  onConfirm: (row: FileFormatApiRow) => Promise<unknown>;
  icon: ReactNode;
  title: string;
  description: (code: string, scopeLabel: string) => ReactNode;
  confirmLabel: string;
  loadingLabel: string;
  confirmVariant?: 'default' | 'destructive';
}

export const FileFormatConfirmDialog: React.FC<FileFormatConfirmDialogProps> = ({
  open,
  row,
  onOpenChange,
  onSuccess,
  onConfirm,
  icon,
  title,
  description,
  confirmLabel,
  loadingLabel,
  confirmVariant = 'default',
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      setIsLoading(false);
    }
  }, [open]);

  const handleConfirm = async () => {
    if (isLoading || !row) return;
    setIsLoading(true);
    setError(null);
    try {
      await onConfirm(row);
      onOpenChange(false);
      onSuccess();
    } catch (err) {
      setError(mapFileFormatApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const scopeLabel = row ? formatScopeLabelForDelete(row) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {icon}
            {title}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-3 pt-2 text-sm text-muted-foreground">
              {description(row?.code ?? '', scopeLabel)}
            </div>
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={isLoading || !row}
          >
            {isLoading ? loadingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
